import React, { useState } from 'react';
import type { ReviewType } from '@wineryxplorer/shared';
import { Button } from '../../ui/Button';
import { UserIcon, FlagIcon } from '../../ui/icons';
import { ModerationReasonModal } from './ModerationReasonModal';

// Moderation review type from API
export interface ModerationReview {
  _id: string;
  reviewType: ReviewType;
  userId: string;
  userName: string;
  title: string;
  body: string;
  underReview: boolean;
  communityFlag?: string;
  isHidden: boolean;
  createdAt: Date | string;
}

export interface ModerationCardProps {
  review: ModerationReview;
  flagType: 'venueManager' | 'community';
  onApprove: (reviewId: string, reviewType: ReviewType, reason: string) => Promise<void>;
  onRemove: (reviewId: string, reviewType: ReviewType, reason: string) => Promise<void>;
  isProcessing?: boolean;
}

// Helper to format date
const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Helper to get review type label
const getReviewTypeLabel = (type: ReviewType): string => {
  const labels: Record<ReviewType, string> = {
    venue: 'Venue Review',
    event: 'Event Review',
    experience: 'Experience Review',
    wine: 'Wine Review',
  };
  return labels[type];
};

// Helper to get flag type badge
const getFlagBadge = (flagType: 'venueManager' | 'community', communityFlag?: string) => {
  if (flagType === 'venueManager') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
        <UserIcon className="w-3 h-3 mr-1" />
        Venue Manager Flag
      </span>
    );
  }

  const flagLabels: Record<string, { label: string; color: string }> = {
    OFFENSIVE: { label: 'Offensive', color: 'bg-red-100 text-red-800' },
    INAPPROPRIATE: { label: 'Inappropriate', color: 'bg-yellow-100 text-yellow-800' },
    FAKE: { label: 'Fake Review', color: 'bg-gray-100 text-gray-800 border border-gray-200' },
    UNHELPFUL: { label: 'Unhelpful', color: 'bg-gray-100 text-gray-800' },
  };

  const flagInfo = communityFlag ? flagLabels[communityFlag] : { label: 'Community Flag', color: 'bg-primary-100 text-primary-800' };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${flagInfo.color}`}>
      <FlagIcon className="w-3 h-3 mr-1" />
      {flagInfo.label}
    </span>
  );
};

/**
 * Card component for displaying a flagged review with moderation actions.
 * Requirements: 9.1, 10.1
 */
export const ModerationCard = ({
  review,
  flagType,
  onApprove,
  onRemove,
  isProcessing = false,
}: ModerationCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [modalState, setModalState] = useState<{ isOpen: boolean; decision: 'approved' | 'removed' }>({
    isOpen: false,
    decision: 'approved',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Truncate body text for preview
  const maxPreviewLength = 200;
  const shouldTruncate = review.body.length > maxPreviewLength;
  const displayBody =
    shouldTruncate && !isExpanded ? `${review.body.slice(0, maxPreviewLength)}...` : review.body;

  const handleApproveClick = () => {
    setModalState({ isOpen: true, decision: 'approved' });
  };

  const handleRemoveClick = () => {
    setModalState({ isOpen: true, decision: 'removed' });
  };

  const handleModalSubmit = async (reason: string) => {
    setIsSubmitting(true);
    try {
      if (modalState.decision === 'approved') {
        await onApprove(review._id, review.reviewType, reason);
      } else {
        await onRemove(review._id, review.reviewType, reason);
      }
      setModalState({ isOpen: false, decision: 'approved' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    if (!isSubmitting) {
      setModalState({ isOpen: false, decision: 'approved' });
    }
  };

  return (
    <>
      <article className="bg-gray-50 border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">
                {getReviewTypeLabel(review.reviewType)}
              </span>
              {getFlagBadge(flagType, review.communityFlag)}
            </div>
            <time className="text-sm text-gray-500" dateTime={new Date(review.createdAt).toISOString()}>
              {formatDate(review.createdAt)}
            </time>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Author info */}
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
              <UserIcon className="w-4 h-4 text-gray-500" />
            </div>
            <span className="font-medium text-gray-900">{review.userName}</span>
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{review.title}</h3>

          {/* Body */}
          <div className="mb-4">
            <p className="text-gray-700 whitespace-pre-wrap">{displayBody}</p>
            {shouldTruncate && (
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-2 text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                {isExpanded ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>

          {/* Status indicators */}
          <div className="flex flex-wrap gap-2 mb-4">
            {review.isHidden && (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                  <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                </svg>
                Hidden from public
              </span>
            )}
            {review.underReview && (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                Under review
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRemoveClick}
            disabled={isProcessing}
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Remove Review
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleApproveClick}
            disabled={isProcessing}
            className="bg-green-600 hover:bg-green-700"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Approve Review
          </Button>
        </div>
      </article>

      {/* Moderation Reason Modal */}
      <ModerationReasonModal
        isOpen={modalState.isOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        decision={modalState.decision}
        isLoading={isSubmitting}
        reviewTitle={review.title}
      />
    </>
  );
};
